import { Button, Modal, Spinner, Table, Box, Badge, ProgressBar } from "@amzn/awsui-components-react";
import React from "react";
import { useMapReleaseStore } from "../../store/MapReleaseStore";

export interface ApprovalRule {
    ruleName: string;
    requiredApprovals: number;
    approvedBy: string[];
    pendingApprovals: string[];
    ruleSatisfied?: boolean;
}

export interface ApprovalData {
    mapPackString: string;
    reviewId: string;
    status: string;
    lastUpdated: string; // ISO date string (YYYY-MM-DDTHH:mm:ss) 
    approvalRules: ApprovalRule[];
    approvalProgress: string;
    totalApprovedCount: number;
}

const ApprovalRulesModal = React.memo(({ mapPackVersion }: { mapPackVersion: string }) => {
    const [isModalVisible, setModalVisible] = React.useState(false);
    const [approvalData, setApprovalData] = React.useState<ApprovalData | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    // Ref to track current abort controller for request cancellation
    const currentAbortController = React.useRef<AbortController | null>(null);

    const currentRegion = useMapReleaseStore((state) => state.currentRegion);
    const approvalStatus = useMapReleaseStore((state) => state.getConsensusApprovalStatus());
    const fetchConsensusApprovalStatus = useMapReleaseStore((state) => state.fetchConsensusApprovalStatus);

    // Helper function to safely format dates
    const formatDate = React.useCallback((dateString: string): string => {
        const date = new Date(dateString);
        return !isNaN(date.getTime()) 
            ? date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
            : 'N/A';
    }, []);

    // Memoized column definitions for the approval rules table
    const approvalRulesColumnDefinition = React.useMemo(() => [
        {
            id: "ruleName",
            header: "Rule Name",
            cell: (item: ApprovalRule) => item.ruleName
        },
        {
            id: "progress",
            header: "Progress",
            cell: (item: ApprovalRule) => (
                <Box>
                    <ProgressBar
                        value={item.requiredApprovals > 0 ? Math.min(100, (item.approvedBy.length / item.requiredApprovals) * 100) : 100}
                        additionalInfo={`${item.approvedBy.length}/${item.requiredApprovals}`}
                        description={item.ruleSatisfied ? "Complete" : "In Progress"}
                    />
                </Box>
            )
        },
        {
            id: "approvedBy",
            header: "Approved By",
            cell: (item: ApprovalRule) => (
                <Box>
                    {item.approvedBy.length > 0 ? (
                        item.approvedBy.map((approver) => (
                            <Badge key={approver} color="green">{approver}</Badge>
                        ))
                    ) : (
                        <span style={{ color: "#5f6b7a" }}>None</span>
                    )}
                </Box>
            )
        },
        {
            id: "pendingApprovals",
            header: "Pending Approvals",
            cell: (item: ApprovalRule) => (
                <Box>
                    {item.pendingApprovals.length > 0 ? (
                        item.pendingApprovals.map((pending) => (
                            <Badge key={pending} color="blue">{pending}</Badge>
                        ))
                    ) : (
                        <span style={{ color: "#5f6b7a" }}>None</span>
                    )}
                </Box>
            )
        }
    ], []);

    // Function to refresh approval data from backend
    const refreshApprovalData = React.useCallback(async () => {
        // Cancel any existing request
        if (currentAbortController.current) {
            currentAbortController.current.abort();
        }

        setIsLoading(true);
        setError(null);

        // Create abort controller for this request
        const abortController = new AbortController();
        currentAbortController.current = abortController;

        try {
            console.log("Refreshing approval data for region:", currentRegion);
            await fetchConsensusApprovalStatus(currentRegion);
            
            // Only update state if request wasn't aborted
            if (!abortController.signal.aborted) {
                setIsLoading(false);
            }
        } catch (err: any) {
            console.error("Error refreshing approval data:", err);
            
            // Only update state if request wasn't aborted
            if (!abortController.signal.aborted) {
                const errorMessage = err instanceof Error ? err.message : String(err);
                setError(`Failed to fetch approval data: ${errorMessage}`);
                setIsLoading(false);
            }
        }
    }, [currentRegion, fetchConsensusApprovalStatus]);

    // Update approval data when approvalStatus changes
    React.useEffect(() => {
        if (isModalVisible && approvalStatus) {
            const matchingApproval = approvalStatus?.find((item: ApprovalData) => 
                item.mapPackString === mapPackVersion
            );

            if (matchingApproval) {
                setApprovalData(matchingApproval);
                setError(null);
            } else {
                setError("No approval data found for this map pack version");
            }
            setIsLoading(false);
        }

        return () => {
            // Cancel any pending requests
            if (currentAbortController.current) {
                currentAbortController.current.abort();
            }
            setIsLoading(false);
        };
    }, [approvalStatus, mapPackVersion, isModalVisible]);

    const handleViewApprovalRules = React.useCallback(() => {
        setModalVisible(true);
        setIsLoading(true);
        setError(null);
        refreshApprovalData();
    }, [refreshApprovalData]);

    const isRuleSatisfied = React.useCallback((rule: ApprovalRule): boolean => {
        if (typeof rule.ruleSatisfied === 'boolean') {
            return rule.ruleSatisfied;
        }
        return rule.approvedBy && rule.approvedBy.length >= rule.requiredApprovals;
    }, []);

    // Memoized overall status calculation
    const overallStatus = React.useMemo(() => {
        if (!approvalData) return null;
        
        if (approvalData.approvalRules.length === 0) {
            return { color: "grey" as const, text: "No Rules" };
        }
        
        const allSatisfied = approvalData.approvalRules.every(isRuleSatisfied);
        
        if (allSatisfied) {
            return { color: "green" as const, text: "All Rules Satisfied" };
        }
        
        // Check if any rules are satisfied
        const anySatisfied = approvalData.approvalRules.some(isRuleSatisfied);
        
        if (anySatisfied) {
            return { color: "blue" as const, text: "Partially Approved" };
        }
        return { color: "red" as const, text: "Pending Approval" };
    }, [approvalData, isRuleSatisfied]);

    return (
        <>
            <Button iconName="view-horizontal" onClick={handleViewApprovalRules} wrapText={false}>
                View Approval Rules
            </Button>
            <Modal
                visible={isModalVisible}
                onDismiss={() => {
                    setModalVisible(false);
                    setIsLoading(false);
                }}
                header="Consensus Approval Rules"
                size="max"
                footer={
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Button 
                            iconName="refresh" 
                            onClick={refreshApprovalData} 
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            Refresh Approval Data
                        </Button>
                        <Button variant="link" onClick={() => setModalVisible(false)}>
                            Close
                        </Button>
                    </div>
                }
            >
                {isLoading ? (
                    <Box textAlign="center">
                        <Spinner size="normal" />
                        <Box variant="p">Loading approval rules...</Box>
                    </Box>
                ) : error ? (
                    <Box variant="p" color="text-status-error">
                        {error}
                    </Box>
                ) : approvalData ? (
                    <div>
                        {/* Header Information */}
                        <Box margin={{ bottom: "l" }}>
                            <Box variant="h3">Map Pack: {approvalData.mapPackString}</Box>
                            <Box margin={{ top: "xs" }}>
                                {overallStatus && (
                                    <Badge color={overallStatus.color}>
                                        {overallStatus.text}
                                    </Badge>
                                )}
                                <Box margin={{ left: "s" }} display="inline-block">
                                    Overall Progress: {approvalData.approvalProgress || "0/0"}
                                </Box>
                            </Box>
                            <Box margin={{ top: "xs" }}>
                                <Box variant="small">
                                    Review ID: {approvalData.reviewId} | 
                                    Status: {approvalData.status} | 
                                    Last Updated: {approvalData.lastUpdated ? formatDate(approvalData.lastUpdated) : 'N/A'}
                                </Box>
                            </Box>
                        </Box>

                        {/* Summary Cards */}
                        <Box margin={{ bottom: "l" }}>
                            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                <div style={{ border: "1px solid #e9ebed", borderRadius: "4px", minWidth: "150px", padding: "12px" }}>
                                    <Box variant="h4">{approvalData.totalApprovedCount || 0}</Box>
                                    <Box variant="small">Total Approved</Box>
                                </div>
                                <div style={{ border: "1px solid #e9ebed", borderRadius: "4px", minWidth: "150px", padding: "12px" }}>
                                    <Box variant="h4">{approvalData.approvalRules?.length || 0}</Box>
                                    <Box variant="small">Total Rules</Box>
                                </div>
                            </div>
                        </Box>

                        {/* Approval Rules Table */}
                        {approvalData.approvalRules?.length > 0 ? (
                            <Table
                                columnDefinitions={approvalRulesColumnDefinition}
                                items={approvalData.approvalRules}
                                header={<Box variant="h3">Approval Rules Details</Box>}
                                empty={
                                    <Box textAlign="center">
                                        <Box variant="strong">No approval rules</Box>
                                        <Box variant="p">No approval rules are configured for this review.</Box>
                                    </Box>
                                }
                            />
                        ) : (
                            <Box textAlign="center" margin={{ top: "l" }}>
                                <Box variant="p">This review does not have any approval rules configured.</Box>
                            </Box>
                        )}
                    </div>
                ) : (
                    <Box variant="p">No approval data available</Box>
                )}
            </Modal>
        </>
    );
});

ApprovalRulesModal.displayName = 'ApprovalRulesModal';

export default ApprovalRulesModal;
